// Imports for external libraries go here.
import React, { FC } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { OneUpHighlightModel, OneUpHighlightProps } from './OneUpHighlight.types';
import { StyledOneUpHighlight } from './OneUpHighlight.styles';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import { Button, Image, VideoMolecule } from '@marriott/mi-ui-library';
import DOMPurify from 'isomorphic-dompurify';
import { useDCADynamicModelAlloy } from '../../modules/helpers';

// Use named rather than default exports.
export const OneUpHighlight: FC<OneUpHighlightProps> = (props: OneUpHighlightProps) => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const componentValues: OneUpHighlightModel = targetData ? targetData : props?.model;

  const sanitizedNode = DOMPurify.sanitize(componentValues?.description as string, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString = sanitizedNode?.innerHTML;

  return (
    <StyledOneUpHighlight className={componentValues?.styleclass}>
      <div className={`OneUpHighlight container px-0`}>
        <div
          className={clsx(
            'row px-0 luxury-OneUpHighlight',
            componentValues?.isPrimaryAssetRight && 'flex-xl-row-reverse'
          )}
        >
          {componentValues && (
            <>
              <div
                className={clsx(
                  'offset-1 col-10 ',
                  componentValues?.isPrimaryAssetRight ? 'offset-xl-2 col-xl-5 asset-container' : 'offset-xl-0 col-xl-5'
                )}
              >
                {componentValues?.assetProperties?.assetType === 'image' && componentValues?.dynamicImage && (
                  <Image
                    customClass={'luxury-OneUpHighlight-image'}
                    dynamic={true}
                    loading={'lazy'}
                    defaultImageURL={componentValues?.dynamicImage?.assetPath}
                    renditions={componentValues?.dynamicImage?.renditions}
                    altText={componentValues?.dynamicImage?.altText}
                  />
                )}
                {componentValues?.assetProperties?.assetType === 'video' && (
                  <div
                    className={clsx(
                      'luxury-OneUpHighlight-video',
                      componentValues?.assetProperties?.videoThumbnail ? '' : 'no-thumbnail'
                    )}
                  >
                    <VideoMolecule
                      id={`luxury-oneuphighlight-${componentValues.assetProperties?.videoAccountId}`}
                      accountId={componentValues.assetProperties?.videoAccountId ?? ''}
                      videoId={componentValues.assetProperties?.brightcoveVideoId ?? ''}
                      poster={componentValues.assetProperties?.videoThumbnail ?? ''}
                      playerId={componentValues.assetProperties?.playerId ?? 'default'}
                      enableMute={true}
                      enableVideoLoop={true}
                      renditions={componentValues.dynamicThumbnailImage?.renditions}
                    />
                  </div>
                )}
              </div>
              <div
                className={clsx(
                  'mt-5 pt-md-3 pt-xl-0 mt-xl-0 offset-1 col-10',
                  componentValues.isPrimaryAssetRight ? 'offset-xl-0 col-xl-5' : 'offset-xl-2 col-xl-5 text-container'
                )}
              >
                {componentValues.headerText && <div className="t-title-s">{componentValues.headerText}</div>}
                {componentValues.description && (
                  <div
                    className={clsx('t-font-alt-m', componentValues.headerText && 'mt-4')}
                    dangerouslySetInnerHTML={{ __html: fragmentString }}
                  ></div>
                )}
                {componentValues?.ctaProperties?.linkUrl && (
                  <div className={clsx('d-inline-flex mt-4')}>
                    {componentValues.enableTracking && componentValues?.ctaProperties?.linkText && (
                      <Button
                        isLink={true}
                        className={clsx(
                          componentValues?.ctaProperties?.ctaType === 'primaryButton' &&
                            'm-button-primary m-button-l px-4',
                          componentValues?.ctaProperties?.ctaType === 'secondaryButton' &&
                            'm-button-secondary m-button-l px-4',
                          componentValues?.ctaProperties?.ctaType === 'tertiaryLink' &&
                            (componentValues?.ctaProperties?.openInANewTab
                              ? 'm-link-tertiary-button-external'
                              : 'm-link-tertiary-button'),
                          componentValues?.ctaProperties?.ctaType === 'textLink' &&
                            'text-link m-link pb-0 pt-2 text-center',
                          componentValues?.ctaProperties?.ctaType === 'textLink' &&
                            componentValues?.ctaProperties?.openInANewTab &&
                            'm-link-tertiary-button-external'
                        )}
                        href={componentValues?.ctaProperties?.linkUrl}
                        custom_click_track_value={componentValues.clickTrackValue}
                        target={componentValues?.ctaProperties?.openInANewTab ? '_blank' : ''}
                        rel={componentValues?.ctaProperties?.openInANewTab ? 'noopener noreferrer' : ''}
                        ariaLabel={componentValues?.ctaProperties?.linkText}
                      >
                        {componentValues?.ctaProperties?.linkText}
                      </Button>
                    )}
                    {!componentValues.enableTracking && componentValues?.ctaProperties?.linkText && (
                      <Button
                        isLink={true}
                        className={clsx(
                          componentValues?.ctaProperties?.ctaType === 'tertiaryLink'
                            ? componentValues?.ctaProperties?.openInANewTab
                              ? 'm-link-tertiary-button-external'
                              : 'm-link-tertiary-button'
                            : componentValues?.ctaProperties?.ctaType === 'primaryButton'
                            ? 'm-button-primary m-button-l px-4'
                            : 'm-button-secondary m-button-l px-4',
                          componentValues?.ctaProperties?.ctaType === 'textLink' &&
                            'text-link m-link pb-0 pt-2 text-center'
                        )}
                        href={componentValues?.ctaProperties?.linkUrl}
                        target={componentValues?.ctaProperties?.openInANewTab ? '_blank' : ''}
                        rel={componentValues?.ctaProperties?.openInANewTab ? 'noopener noreferrer' : ''}
                        ariaLabel={componentValues?.ctaProperties?.linkText}
                      >
                        {componentValues?.ctaProperties?.linkText}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </StyledOneUpHighlight>
  );
};

export const OneUpHighlightConfig = {
  emptyLabel: 'LuxuryOneUpHighlight',
  isEmpty: () => true,
  resourceType: `mi-aem-luxury-spa/components/content/OneUpHighlight/v1/OneUpHighlight`,
};
//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OneUpHighlightEditable = (props: any) => (
  <div data-testid="luxury-OneUpHighlight" data-component-name="m-luxury-OneUpHighlight">
    <EditableComponent config={OneUpHighlightConfig} {...props}>
      <OneUpHighlight {...props} />
    </EditableComponent>
  </div>
);
